/**
 * Information about the start and end dates of the first and last
 * [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction)
 * that belong to a [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction)
 * and whether any events follow an unlimited repetition pattern.
 */
export interface ScheduleTimeFrame {
  /**
   * ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)
   * to which the time frame belongs.
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /**
   * Information about the presence of recurring events within the schedule.
   *
   * Supported values:
   * + `NONE`: No [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/event-object) are scheduled within the schedule.
   * + `FINITE`: All recurring events within the schedule have a defined end date, or there are no recurring events.
   * + `INFINITE`: At least one recurring event within the schedule has no end date, meaning it follows an unlimited repetition pattern.
   * @readonly
   */
  status?: StatusWithLiterals;
  /**
   * Start date of the first [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/event-object)
   * within the schedule. Identical to `event.start.localDate`. Available only if
   * `status` is set to `FINITE` or `INFINITE`.
   * @readonly
   */
  firstEventStartDate?: ZonedDate;
  /**
   * End date of the last [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/event-object)
   * belonging to the schedule. Identical to `event.end.localDate`. Available only if
   * `status` is set to `FINITE`.
   * @readonly
   */
  lastEventEndDate?: ZonedDate;
  /**
   * Start date of the first [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/event-object)
   * belonging to the schedule, adjusted to `timeZone` of the
   * [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)
   * or the `timeZone` specified in the request.
   * Identical to `event.adjustedStart.localDate`.
   * @readonly
   */
  adjustedFirstEventStartDate?: ZonedDate;
  /**
   * End date of the last [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/event-object)
   * belonging to the schedule, adjusted to `timeZone` of the
   * [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)
   * or the `timeZone` specified in the request.
   * Identical to `event.adjustedEnd.localDate`.
   * @readonly
   */
  adjustedLastEventEndDate?: ZonedDate;
  /**
   * Revision number, which increments by 1 each time the schedule time frame is updated.
   * @readonly
   */
  revision?: string | null;
}

export enum Status {
  /** No [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/event-object) that belong to the schedule. */
  NONE = 'NONE',
  /** There are no recurring events or all recurring events belonging to the schedule have a defined end date. */
  FINITE = 'FINITE',
  /** At least one recurring event belonging to the schedule has an unlimited repetition pattern (no end date). */
  INFINITE = 'INFINITE',
}

/** @enumType */
export type StatusWithLiterals = Status | 'NONE' | 'FINITE' | 'INFINITE';

/** A date time with a time zone, having the UTC offset and date determined by the server. */
export interface ZonedDate {
  /**
   * Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).
   * For example, `2024-01-30T13:30:00`.
   * Wix Calendar APIs ignore seconds.
   * @format LOCAL_DATE_TIME
   */
  localDate?: string | null;
  /**
   * Time zone in
   * [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).
   * For example, `America/New_York`.
   * @readonly
   */
  timeZone?: string | null;
  /**
   * UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).
   * For example, `2024-01-30T13:30:00`.
   * Not available for adjusted date fields.
   * @readonly
   */
  utcDate?: Date | null;
}

export interface GetScheduleTimeFrameRequest {
  /**
   * [Schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)
   * ID for which to retrieve the schedule time frame.
   * @format GUID
   */
  id: string | null;
  /**
   * Time zone for adjusting the times of the returned schedule time frame.
   *
   * Default: `timeZone` of the [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)
   */
  timeZone?: string | null;
}

export interface GetScheduleTimeFrameResponse {
  /** Retrieved schedule time frame. */
  scheduleTimeFrame?: ScheduleTimeFrame;
}

export interface ListScheduleTimeFramesRequest {
  /**
   * IDs of the schedules for which to retrieve schedule time frames.
   *
   * Min: 1 schedule ID
   * Max: 100 schedule IDs
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  ids: string[];
  /**
   * Time zone for adjusting the times of the returned schedule time frames.
   *
   * Default: `timeZone` of the [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)
   */
  timeZone?: string | null;
}

export interface ListScheduleTimeFramesResponse {
  /** Retrieved schedule time frames. */
  scheduleTimeFrames?: ScheduleTimeFrame[];
}

/** Encapsulates all details written to the Greyhound topic when a site's properties are updated. */
export interface SitePropertiesNotification {
  /** The site ID for which this update notification applies. */
  metasiteId?: string;
  /** The actual update event. */
  event?: SitePropertiesEvent;
  /**
   * A convenience set of mappings from the MetaSite ID to its constituent services.
   * @maxSize 500
   */
  translations?: Translation[];
  /** Context of the notification */
  changeContext?: ChangeContext;
}

/** The actual update event for a particular notification. */
export interface SitePropertiesEvent {
  /** Version of the site's properties represented by this update. */
  version?: number;
  /** Set of properties that were updated - corresponds to the fields in "properties". */
  fields?: string[];
  /** Updated properties. */
  properties?: Properties;
}

export interface Properties {
  /** Site categories. */
  categories?: Categories;
  /** Site locale. */
  locale?: Locale;
  /**
   * Site language.
   *
   * Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format.
   */
  language?: string | null;
  /**
   * Site currency format used to bill customers.
   *
   * Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.
   */
  paymentCurrency?: string | null;
  /** Timezone in `America/New_York` format. */
  timeZone?: string | null;
  /** Email address. */
  email?: string | null;
  /** Phone number. */
  phone?: string | null;
  /** Fax number. */
  fax?: string | null;
  /** Address. */
  address?: Address;
  /** Site display name. */
  siteDisplayName?: string | null;
  /** Business name. */
  businessName?: string | null;
  /** Path to the site's logo in Wix Media (without Wix Media base URL). */
  logo?: string | null;
  /** Site description. */
  description?: string | null;
  /**
   * Business schedule. Regular and exceptional time periods when the business is open or the service is available.
   *
   * __Note:__ Not supported by Wix Bookings.
   */
  businessSchedule?: BusinessSchedule;
  /** Supported languages of a site and the primary language. */
  multilingual?: Multilingual;
  /** Cookie policy the Wix user defined for their site (before the site visitor interacts with/limits it). */
  consentPolicy?: ConsentPolicy;
  /**
   * Supported values: `FITNESS SERVICE`, `RESTAURANT`, `BLOG`, `STORE`, `EVENT`, `UNKNOWN`.
   *
   * Site business type.
   */
  businessConfig?: string | null;
  /** External site URL that uses Wix as its headless business solution. */
  externalSiteUrl?: string | null;
  /** Track clicks analytics. */
  trackClicksAnalytics?: boolean;
  /**
   * Company ID issued to the business by local authorities. Appears on invoices and price quotes.
   * @maxLength 50
   */
  companyId?: string | null;
}

export interface Categories {
  /** Primary site category. */
  primary?: string;
  /**
   * Secondary site category.
   * @maxSize 50
   */
  secondary?: string[];
  /** Business Term Id */
  businessTermId?: string | null;
}

export interface Locale {
  /** Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format. */
  languageCode?: string;
  /** Two-letter country code in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements) format. */
  country?: string;
}

export interface Address {
  /** Street name. */
  street?: string;
  /** City name. */
  city?: string;
  /** Two-letter country code in an [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format. */
  country?: string;
  /** State. */
  state?: string;
  /**
   * Zip or postal code.
   * @maxLength 20
   */
  zip?: string;
  /** Extra information to be displayed in the address. */
  hint?: AddressHint;
  /** Whether this address represents a physical location. */
  isPhysical?: boolean;
  /** Google-formatted version of this address. */
  googleFormattedAddress?: string;
  /** Street number. */
  streetNumber?: string;
  /** Apartment number. */
  apartmentNumber?: string;
  /** Geographic coordinates of location. */
  coordinates?: GeoCoordinates;
}

/**
 * Extra information on displayed addresses.
 * This is used for display purposes. Used to add additional data about the address, such as "In the passage".
 * Free text. In addition, the user can state where to display the additional description - before, after, or instead of the address string.
 */
export interface AddressHint {
  /** Extra text displayed next to, or instead of, the actual address. */
  text?: string;
  /** Where the extra text should be displayed. */
  placement?: PlacementTypeWithLiterals;
}

/** Where the extra text should be displayed: before, after or instead of the actual address. */
export enum PlacementType {
  BEFORE = 'BEFORE',
  AFTER = 'AFTER',
  REPLACE = 'REPLACE',
}

/** @enumType */
export type PlacementTypeWithLiterals =
  | PlacementType
  | 'BEFORE'
  | 'AFTER'
  | 'REPLACE';

/** Geocoordinates for a particular address. */
export interface GeoCoordinates {
  /** Latitude of the location. Must be between -90 and 90. */
  latitude?: number;
  /** Longitude of the location. Must be between -180 and 180. */
  longitude?: number;
}

/** Business schedule. Regular and exceptional time periods when the business is open or the service is available. */
export interface BusinessSchedule {
  /**
   * Weekly recurring time periods when the business is regularly open or the service is available. Limited to 100 time periods.
   * @maxSize 100
   */
  periods?: TimePeriod[];
  /**
   * Exceptions to the business's regular hours. The business can be open or closed during the exception.
   * @maxSize 100
   */
  specialHourPeriod?: SpecialHourPeriod[];
}

/** Weekly recurring time periods when the business is regularly open or the service is available. */
export interface TimePeriod {
  /** Day of the week the period starts on. */
  openDay?: DayOfWeekWithLiterals;
  /**
   * Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents
   * midnight at the end of the specified day.
   */
  openTime?: string;
  /** Day of the week the period ends on. */
  closeDay?: DayOfWeekWithLiterals;
  /**
   * Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents
   * midnight at the end of the specified day.
   *
   * __Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.
   */
  closeTime?: string;
}

/** Enumerates the days of the week. */
export enum DayOfWeek {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY',
}

/** @enumType */
export type DayOfWeekWithLiterals =
  | DayOfWeek
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

/** Exception to the business's regular hours. The business can be open or closed during the exception. */
export interface SpecialHourPeriod {
  /** Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). */
  startDate?: string;
  /** End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). */
  endDate?: string;
  /**
   * Whether the business is closed (or the service is not available) during the exception.
   *
   * Default: `true`.
   */
  isClosed?: boolean;
  /** Additional info about the exception. For example, "We close earlier on New Year's Eve." */
  comment?: string;
}

export interface Multilingual {
  /**
   * Supported languages list.
   * @maxSize 200
   */
  supportedLanguages?: SupportedLanguage[];
  /** Whether to redirect to user language. */
  autoRedirect?: boolean;
}

export interface SupportedLanguage {
  /** Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format. */
  languageCode?: string;
  /** Locale. */
  locale?: Locale;
  /** Whether the supported language is the primary language for the site. */
  isPrimary?: boolean;
  /** Language icon. */
  countryCode?: string;
  /** How the language will be resolved. For internal use. */
  resolutionMethod?: ResolutionMethodWithLiterals;
  /** Whether the supported language is the primary language for site visitors. */
  isVisitorPrimary?: boolean | null;
}

export enum ResolutionMethod {
  QUERY_PARAM = 'QUERY_PARAM',
  SUBDOMAIN = 'SUBDOMAIN',
  SUBDIRECTORY = 'SUBDIRECTORY',
}

/** @enumType */
export type ResolutionMethodWithLiterals =
  | ResolutionMethod
  | 'QUERY_PARAM'
  | 'SUBDOMAIN'
  | 'SUBDIRECTORY';

export interface ConsentPolicy {
  /** Whether the site uses cookies that are essential to site operation. Always `true`. */
  essential?: boolean | null;
  /** Whether the site uses cookies that affect site performance and other functional measurements. */
  functional?: boolean | null;
  /** Whether the site uses cookies that collect analytics about how the site is used (in order to improve it). */
  analytics?: boolean | null;
  /** Whether the site uses cookies that collect information allowing better customization of the experience for a current visitor. */
  advertising?: boolean | null;
  /** CCPA compliance flag. */
  dataToThirdParty?: boolean | null;
}

/** A single mapping from the MetaSite ID to a particular service. */
export interface Translation {
  /** The service type. */
  serviceType?: string;
  /** The application definition ID; this only applies to services of type ThirdPartyApps. */
  appDefId?: string;
  /** The instance ID of the service. */
  instanceId?: string;
}

export interface ChangeContext extends ChangeContextPayloadOneOf {
  /** Properties were updated. */
  propertiesChange?: PropertiesChange;
  /** Default properties were created on site creation. */
  siteCreated?: SiteCreated;
  /** Properties were cloned on site cloning. */
  siteCloned?: SiteCloned;
}

/** @oneof */
export interface ChangeContextPayloadOneOf {
  /** Properties were updated. */
  propertiesChange?: PropertiesChange;
  /** Default properties were created on site creation. */
  siteCreated?: SiteCreated;
  /** Properties were cloned on site cloning. */
  siteCloned?: SiteCloned;
}

export interface PropertiesChange {}

export interface SiteCreated {
  /** Origin template site id. */
  originTemplateId?: string | null;
}

export interface SiteCloned {
  /** Origin site id. */
  originMetaSiteId?: string;
}

export interface Empty {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}
